/**
 * ErrorBoundary Component
 * Catches React runtime errors and prevents app crashes
 * Protects user session and provides graceful error recovery
 */
import React from 'react';
import logger from '../utils/logger';
import * as chunkReload from '../utils/chunkReload';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      reloading: false
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so next render shows fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // A deploy replaced the code files this tab still asks for: load the new version once
    // instead of showing an error (utils/chunkReload.js).
    if (chunkReload.reloadOnceForNewVersion(error)) {
      logger.warn('ErrorBoundary', 'New version deployed — reloading once', { error: error.message });
      this.setState({ reloading: true });
      return;
    }
    // Log error details
    logger.error('ErrorBoundary', 'Runtime error caught', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });
  }

  handleRetry = () => {
    // Reset error state and retry
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    // Full page reload (preserves session in localStorage)
    window.location.reload();
  };

  render() {
    if (this.state.reloading) return null;   // the page is reloading; no error flash
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.iconContainer}>
              <span style={styles.icon}>⚠️</span>
            </div>
            <h1 style={styles.title}>Something went wrong</h1>
            <p style={styles.message}>
              We encountered an unexpected error. Please try again or reload the page.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={styles.details}>
                <summary style={styles.summary}>Error details</summary>
                <pre style={styles.errorText}>{this.state.error.toString()}</pre>
              </details>
            )}
            <div style={styles.buttonContainer}>
              <button onClick={this.handleRetry} style={styles.buttonPrimary}>
                Try Again
              </button>
              <button onClick={this.handleReload} style={styles.buttonSecondary}>
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Inline styles (no CSS dependency)
const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    padding: '20px'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    textAlign: 'center'
  },
  iconContainer: {
    marginBottom: '20px'
  },
  icon: {
    fontSize: '64px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1b2920',
    marginBottom: '12px'
  },
  message: {
    fontSize: '16px',
    color: '#6c757d',
    marginBottom: '24px',
    lineHeight: '1.5'
  },
  details: {
    marginTop: '20px',
    marginBottom: '20px',
    textAlign: 'left',
    backgroundColor: '#f8f9fa',
    padding: '15px',
    borderRadius: '8px'
  },
  summary: {
    cursor: 'pointer',
    fontWeight: '500',
    color: '#495057',
    marginBottom: '10px'
  },
  errorText: {
    fontSize: '12px',
    color: '#dc3545',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginTop: '10px'
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center'
  },
  buttonPrimary: {
    backgroundColor: '#1b2920',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  buttonSecondary: {
    backgroundColor: 'transparent',
    color: '#1b2920',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  }
};

export default ErrorBoundary;
